import { useMemo, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/utils';
import {
  useCSCategoryPropagateBusinessUnits,
  usePreviewCSCategoryPropagate,
  useApplyCSCategoryPropagate
} from '@/hooks';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import type { CSCategoryPropagateAction, CSCategoryPropagateScope, CSCategoryPropagateTarget } from '@/types';

interface CSIntentPropagateDialogProps {
  /** The template that was just created/edited/enabled/disabled/deleted. Null closes the dialog. */
  templateId: string | null;
  action: CSCategoryPropagateAction | null;
  onOpenChange: (open: boolean) => void;
}

type Stage = 'select' | 'preview';

const ACTION_LABEL: Record<CSCategoryPropagateAction, string> = {
  create: 'created',
  update: 'updated',
  disable: 'disabled',
  enable: 'enabled',
  delete: 'deleted'
};

function groupByAccount(targets: CSCategoryPropagateTarget[]): Map<number, { accountName: string; units: CSCategoryPropagateTarget[] }> {
  const map = new Map<number, { accountName: string; units: CSCategoryPropagateTarget[] }>();
  for (const target of targets) {
    const existing = map.get(target.accountId);
    if (existing) {
      existing.units.push(target);
    } else {
      map.set(target.accountId, { accountName: target.accountName, units: [target] });
    }
  }
  return map;
}

export function CSIntentPropagateDialog({ templateId, action, onOpenChange }: CSIntentPropagateDialogProps) {
  const [stage, setStage] = useState<Stage>('select');
  const [scope, setScope] = useState<CSCategoryPropagateScope>('none');
  const [selectedBuIds, setSelectedBuIds] = useState<Set<number>>(new Set());
  const [previewTargets, setPreviewTargets] = useState<CSCategoryPropagateTarget[]>([]);
  const [previewSkipped, setPreviewSkipped] = useState<CSCategoryPropagateTarget[]>([]);

  const { data: businessUnits = [], isLoading: businessUnitsLoading } = useCSCategoryPropagateBusinessUnits();
  const previewMutation = usePreviewCSCategoryPropagate();
  const applyMutation = useApplyCSCategoryPropagate();

  const open = !!templateId && !!action;

  useEffect(() => {
    if (open) {
      setStage('select');
      setScope('none');
      setSelectedBuIds(new Set());
      setPreviewTargets([]);
      setPreviewSkipped([]);
    }
  }, [open, templateId]);

  const groupedBusinessUnits = useMemo(() => groupByAccount(businessUnits), [businessUnits]);

  function toggleBuId(buId: number) {
    setSelectedBuIds((prev) => {
      const next = new Set(prev);
      if (next.has(buId)) next.delete(buId);
      else next.add(buId);
      return next;
    });
  }

  async function handlePreview() {
    if (!templateId || !action) return;
    if (scope === 'none') {
      onOpenChange(false);
      return;
    }
    try {
      const result = await previewMutation.mutateAsync({
        id: templateId,
        payload: { action, scope, buIds: scope === 'selected' ? Array.from(selectedBuIds) : undefined }
      });
      setPreviewTargets(result.targets);
      setPreviewSkipped(result.skippedCustomized);
      setStage('preview');
    } catch (err) {
      console.error('CSIntentPropagateDialog.handlePreview failed', { templateId, action, scope, error: err });
      toast.error(getApiErrorMessage(err, 'Failed to preview propagation'));
    }
  }

  async function handleConfirm() {
    if (!templateId || !action) return;
    try {
      const result = await applyMutation.mutateAsync({
        id: templateId,
        payload: { action, scope, buIds: scope === 'selected' ? Array.from(selectedBuIds) : undefined }
      });
      toast.success(`Propagated to ${result.updated} business unit(s)`);
      onOpenChange(false);
    } catch (err) {
      console.error('CSIntentPropagateDialog.handleConfirm failed', { templateId, action, scope, error: err });
      toast.error(getApiErrorMessage(err, 'Failed to apply propagation'));
    }
  }

  const canPreview = scope === 'none' || scope === 'all' || (scope === 'selected' && selectedBuIds.size > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[520px]'>
        <DialogHeader>
          <DialogTitle>Apply to business units</DialogTitle>
          <DialogDescription>
            The template intent was {action ? ACTION_LABEL[action] : ''}. Choose which business units should receive this change.
          </DialogDescription>
        </DialogHeader>

        {stage === 'select' ? (
          <div className='space-y-4'>
            <RadioGroup value={scope} onValueChange={(value) => setScope(value as CSCategoryPropagateScope)}>
              <div className='flex items-center gap-2'>
                <RadioGroupItem value='all' id='propagate-all' />
                <Label htmlFor='propagate-all' className='font-normal'>All business units</Label>
              </div>
              <div className='flex items-center gap-2'>
                <RadioGroupItem value='selected' id='propagate-selected' />
                <Label htmlFor='propagate-selected' className='font-normal'>Selected business units</Label>
              </div>
              <div className='flex items-center gap-2'>
                <RadioGroupItem value='none' id='propagate-none' />
                <Label htmlFor='propagate-none' className='font-normal'>Don&apos;t apply</Label>
              </div>
            </RadioGroup>

            {scope === 'selected' && (
              <ScrollArea className='h-[240px] rounded-md border border-border p-3'>
                {businessUnitsLoading ? (
                  <div className='flex items-center justify-center py-8'>
                    <Spinner className='size-5' />
                  </div>
                ) : groupedBusinessUnits.size === 0 ? (
                  <p className='text-sm text-muted-foreground'>No business units found.</p>
                ) : (
                  <div className='space-y-4'>
                    {Array.from(groupedBusinessUnits.entries()).map(([accountId, { accountName, units }]) => (
                      <div key={accountId} className='space-y-2'>
                        <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>{accountName}</p>
                        {units.map((unit) => (
                          <div key={unit.buId} className='flex items-center gap-2 pl-1'>
                            <Checkbox
                              id={`bu-${unit.buId}`}
                              checked={selectedBuIds.has(unit.buId)}
                              onCheckedChange={() => toggleBuId(unit.buId)}
                            />
                            <Label htmlFor={`bu-${unit.buId}`} className='font-normal text-sm'>
                              {unit.buName}
                              {unit.isDefault && <span className='text-muted-foreground'> (default)</span>}
                            </Label>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            )}
          </div>
        ) : (
          <div className='space-y-3'>
            <p className='text-sm'>
              This will apply to <strong>{previewTargets.length}</strong> business unit{previewTargets.length !== 1 ? 's' : ''}.
            </p>
            {previewSkipped.length > 0 && (
              <div className='rounded-md border border-orange-300 bg-orange-50 p-3 text-xs text-orange-700 leading-relaxed'>
                <p className='font-semibold mb-1'>
                  {previewSkipped.length} customized cop{previewSkipped.length !== 1 ? 'ies' : 'y'} will be skipped:
                </p>
                <ul className='list-disc pl-4 space-y-0.5'>
                  {previewSkipped.map((target) => (
                    <li key={`${target.accountId}-${target.buId}`}>
                      {target.accountName} / {target.buName}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {previewTargets.length > 0 && (
              <ScrollArea className='h-[160px] rounded-md border border-border p-3'>
                <ul className='space-y-1 text-sm text-muted-foreground'>
                  {previewTargets.map((target) => (
                    <li key={`${target.accountId}-${target.buId}`}>
                      {target.accountName} / {target.buName}
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            )}
          </div>
        )}

        <DialogFooter>
          {stage === 'select' ? (
            <>
              <Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type='button' onClick={handlePreview} disabled={!canPreview || previewMutation.isPending}>
                {scope === 'none' ? 'Done' : previewMutation.isPending ? 'Loading...' : 'Preview'}
              </Button>
            </>
          ) : (
            <>
              <Button type='button' variant='outline' onClick={() => setStage('select')}>
                Back
              </Button>
              <Button type='button' onClick={handleConfirm} disabled={applyMutation.isPending || previewTargets.length === 0}>
                {applyMutation.isPending ? 'Applying...' : 'Confirm'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
